import { type FC, memo, type ReactNode, useCallback, useState } from 'react'

import { useIsMobile } from '@/hooks/use-mobile.hook'
import { cn } from '@/lib/utils'

import type { ChatInputMode } from './chat-input-mode.type'
import { ChatInputModeSwitch } from './chat-input-mode-switch.atom'
import { ChatInputSendButton } from './chat-input-send-button.atom'
import { ChatInputStopButton } from './chat-input-stop-button.atom'
import { ChatInputToolbarAttachment } from './chat-input-toolbar-attachment.atom'
import type { PromptTemplate } from './prompt-template.interface'
import { PromptTemplatePopover } from './prompt-template-popover.molecule'

export interface ChatInputToolbarProps {
  mode: ChatInputMode
  onModeChange: (mode: ChatInputMode) => void
  onAttachFromComputer: () => void
  onAttachFromChats: () => void
  onSend: () => void
  canSend: boolean
  isLoading?: boolean
  isDisabled?: boolean
  isFileAttachEnabled?: boolean
  isNoteMode?: boolean
  isStreaming?: boolean
  isStopping?: boolean
  onStop?: () => void
  className?: string
  templateList?: PromptTemplate[]
  onTemplateSelect?: (templateId: string) => void
  onCreateTemplate?: () => void
}

interface ActionButtonProps {
  isStreaming?: boolean
  isStopping?: boolean
  onStop?: () => void
  canSend: boolean
  isLoading: boolean
  isNoteMode: boolean
  onSend: () => void
}

const ActionButton: FC<ActionButtonProps> = (p): ReactNode => {
  if (p.isStreaming && p.onStop) {
    return <ChatInputStopButton onClick={p.onStop} isStopping={p.isStopping ?? false} />
  }
  return (
    <ChatInputSendButton
      canSend={p.canSend}
      isLoading={p.isLoading}
      isNoteMode={p.isNoteMode}
      onClick={p.onSend}
    />
  )
}

interface TemplateSection {
  templateList?: PromptTemplate[]
  onTemplateSelect?: (templateId: string) => void
  onCreateTemplate?: () => void
  isDisabled: boolean
}

const TemplatePopover: FC<TemplateSection> = ({
  templateList,
  onTemplateSelect,
  onCreateTemplate,
  isDisabled,
}): ReactNode => {
  if (!templateList?.length || !onTemplateSelect) return null
  return (
    <PromptTemplatePopover
      templateList={templateList}
      onSelect={onTemplateSelect}
      onCreateNew={onCreateTemplate}
      isDisabled={isDisabled}
    />
  )
}

const ChatInputToolbarBase: FC<ChatInputToolbarProps> = (props): ReactNode => {
  const { canSend, isDisabled = false, isFileAttachEnabled = true, isLoading = false } = props
  const isMobile = useIsMobile()
  const [isSheetOpen, setIsSheetOpen] = useState(false)
  const canActuallySend = canSend && !isDisabled && !isLoading

  const handleSendClick = useCallback(() => {
    if (canActuallySend) props.onSend()
  }, [canActuallySend, props.onSend])

  return (
    <div
      className={cn(
        'chat-input-toolbar flex items-center gap-2',
        isDisabled && 'pointer-events-none opacity-50',
        props.className
      )}
    >
      <ChatInputToolbarAttachment
        isMobile={isMobile}
        isSheetOpen={isSheetOpen}
        onSheetOpenChange={setIsSheetOpen}
        onSelectFromComputer={props.onAttachFromComputer}
        onSelectFromChats={props.onAttachFromChats}
        isDisabled={isDisabled}
        isFileAttachEnabled={isFileAttachEnabled}
      />
      <ChatInputModeSwitch
        mode={props.mode}
        onModeChange={props.onModeChange}
        isDisabled={isDisabled}
      />
      <TemplatePopover
        templateList={props.templateList}
        onTemplateSelect={props.onTemplateSelect}
        onCreateTemplate={props.onCreateTemplate}
        isDisabled={isDisabled}
      />
      <div className="flex-1" />
      <ActionButton
        isStreaming={props.isStreaming}
        isStopping={props.isStopping}
        onStop={props.onStop}
        canSend={canActuallySend}
        isLoading={isLoading}
        isNoteMode={props.isNoteMode ?? false}
        onSend={handleSendClick}
      />
    </div>
  )
}

export const ChatInputToolbar = memo(ChatInputToolbarBase)

ChatInputToolbar.displayName = 'ChatInputToolbar'
